import clsx from "clsx";
import { type FC, useEffect } from "react";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { AssessmentStatusIcon } from "components/materials/page/assessment/AssessmentStatusIcon";
import { getSubmissionTimeText } from "components/materials/page/helpers";
import type { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import { Menu, MenuItem } from "mds/components/Menu";
import { ChevronDownIcon, VerifiedIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectFullSubmissionById, selectSortedUserFullSubmissionsByPageId } from "store/selectors";
import type { FullGroupSubmissionType, FullIndividualSubmissionType } from "store/selectors/types";
import { useQueryParamsManager } from "utils/routing";

type SubmissionMenuItemType = "draft" | FullIndividualSubmissionType | FullGroupSubmissionType;

type SubmissionItemContentProps = {
  submission: FullIndividualSubmissionType | FullGroupSubmissionType;
  selectedAccessId?: string;
  assessmentsPublished: boolean;
  canAuthorCourse: boolean;
  page: PageType;
  selected?: boolean;
  submissionIndex: number;
  total: number;
};

const SubmissionItemContent: FC<SubmissionItemContentProps> = ({
  submission,
  selectedAccessId,
  assessmentsPublished,
  canAuthorCourse,
  page,
  selected = false,
  submissionIndex,
  total,
}) => {
  return (
    <div className="flex items-center gap-2">
      <IconText
        iconEnd={
          <AssessmentStatusIcon
            accessId={selectedAccessId}
            assessmentsPublished={assessmentsPublished}
            canAuthorCourse={canAuthorCourse}
            className={clsx({ "text-green": assessmentsPublished })}
            page={page}
            selected={selected}
            selectedSubmission={submission}
            showLate
          />
        }
        text={`${t("submission_menu.submission_title", {
          number: submissionIndex + 1,
          total,
        })} - ${getSubmissionTimeText(submission)}`}
      />

      {submission.official_at && <VerifiedIcon className="text-green" />}
    </div>
  );
};

export type SubmissionMenuProps = {
  page: PageType;
  selectedAccessId?: string;
  showDraft?: boolean;
  isAssessing?: boolean;
};

/**
 * A dropdown menu that allows the user to select a submission to view. The menu assumes that there
 * is always at least one submission in the submissions list by the time the user is viewing this menu.
 * There are multiple ways to see this dropdown, 1) the student is on their My Submission tab, 2)
 * the instructor is in assessment mode, or 3) the instructor is on the Class Responses tab (showResponses) and
 * there are student submissions (where the dropdown menu will show both Draft and all the student's submissions).
 *
 * Seeing either the My Submission tab or accessing assessment mode are gated by whether there are any
 * submissions.
 *
 * By nature of how we collect submissions, there should also always be a submission for each student
 * by the time we reach this menu. There are two ways to collect submissions:
 * - the "Collect" button from the instructor, which will collect a submission per student in the course
 * - automatically collecting a submission per student on due date
 *
 * There should be no early submission for an individual student, and even if a student turns in a late
 * submission late, there should already be a default submission collected on due date. */
export const SubmissionMenu: FC<SubmissionMenuProps> = ({
  page,
  selectedAccessId,
  showDraft = false,
  isAssessing = false,
}) => {
  const { setQueryParams, getQueryParam } = useQueryParamsManager();
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const currentSubmission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));
  const { canAuthorCourse } = useCapabilities();
  const submissions = useAppSelector((s) =>
    selectSortedUserFullSubmissionsByPageId(s, page.id, selectedAccessId),
  );
  const submissionIndex = submissions.findIndex((s) => s.id === submissionId);

  const officialSubmission = submissions.find((s) => s.official_at);

  // If the user is assessing, the latest submission should be selected by default.
  //   NOTE: this code only applies for the first student being assessed. Subsequent
  //   students will have their default submission selected by AssessmentGradingView.
  // Otherwise:
  // If there's an official submission, that should be select by default.
  // If not, and there's at least one unofficial submission, the most recent
  //   unofficial submission should be selected by default.
  // Only if there are no submissions at all, should the draft be selected by default
  //   -- and then, only if we are showing the draft.
  let defaultMenuItem;
  if (isAssessing) {
    defaultMenuItem = submissions.length > 0 ? submissions[submissions.length - 1] : null;
  } else {
    defaultMenuItem =
      submissions.length > 0 ? officialSubmission || submissions[submissions.length - 1] : null; // null means draft
  }

  useEffect(() => {
    if (!submissionId && defaultMenuItem) {
      setQueryParams({ [SUBMISSION_ID_QUERY_PARAM]: defaultMenuItem.id });
    }
  }, [selectedAccessId, submissionId, defaultMenuItem, setQueryParams]);

  const assessmentsPublished = !!page?.assessments_published_at;

  if (submissions.length === 0 || (!currentSubmission && !showDraft)) {
    return null;
  }

  const selectedSubmissionIsDraft = submissionId === "draft";

  const MenuButton = (
    <LegacyButton
      className="gap-1"
      kind="secondary"
      size="s"
      title={
        selectedSubmissionIsDraft
          ? t("submission_menu.draft")
          : t("submission_menu.selected_submission")
      }
    >
      {selectedSubmissionIsDraft ? (
        t("submission_menu.draft")
      ) : currentSubmission ? (
        <SubmissionItemContent
          assessmentsPublished={assessmentsPublished}
          canAuthorCourse={canAuthorCourse}
          page={page}
          selectedAccessId={selectedAccessId}
          submission={currentSubmission}
          submissionIndex={submissionIndex}
          total={submissions.length}
        />
      ) : null}
      <ChevronDownIcon />
    </LegacyButton>
  );

  const handleItemClick = (newSubmission: SubmissionMenuItemType) => {
    if (newSubmission === "draft") {
      setQueryParams({ [SUBMISSION_ID_QUERY_PARAM]: "draft" });
    } else {
      setQueryParams({ [SUBMISSION_ID_QUERY_PARAM]: newSubmission.id });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Menu direction="bottom" menuButton={MenuButton} position="initial">
        {showDraft && (
          <MenuItem
            checked={selectedSubmissionIsDraft}
            className={clsx({
              selected: selectedSubmissionIsDraft,
              "text-white": selectedSubmissionIsDraft,
            })}
            value="draft"
            onClick={() => handleItemClick("draft")}
          >
            {t("submission_menu.draft")}
          </MenuItem>
        )}

        {submissions.map((submission, index) => {
          const selected = !selectedSubmissionIsDraft && currentSubmission?.id === submission.id;
          return (
            <MenuItem
              checked={selected}
              className={clsx("light", {
                selected,
              })}
              eventName="Submission selected"
              key={submission.id}
              onClick={() => handleItemClick(submission)}
            >
              <SubmissionItemContent
                assessmentsPublished={assessmentsPublished}
                canAuthorCourse={canAuthorCourse}
                page={page}
                selected={selected}
                selectedAccessId={selectedAccessId}
                submission={submission}
                submissionIndex={index}
                total={submissions.length}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};
